// src/components/UI.jsx
import React from "react";

export function Spinner({ size = 12, color = "currentColor" }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        border: `1.5px solid ${color}`,
        borderTopColor: "transparent",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
        flexShrink: 0,
      }}
    />
  );
}

export function StatusBar({ type = "info", children }) {
  const styles = {
    info:    { bg: "var(--blue-dim)",   border: "rgba(61,142,240,.25)",   color: "var(--blue)" },
    ok:      { bg: "var(--green-dim)",  border: "rgba(54,179,126,.25)",   color: "var(--green)" },
    warn:    { bg: "var(--warn-dim)",   border: "rgba(212,144,10,.25)",   color: "var(--warn)" },
    err:     { bg: "var(--red-dim)",    border: "rgba(224,82,82,.25)",    color: "var(--red)" },
    idle:    { bg: "var(--s2)",         border: "var(--b1)",              color: "var(--tx3)" },
    purple:  { bg: "var(--purple-dim)", border: "rgba(139,126,248,.25)",  color: "var(--purple)" },
  };
  const s = styles[type] || styles.info;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "9px 12px", borderRadius: "var(--r)",
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      fontSize: 11, marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

export function Card({ title, action, children, noPad = false }) {
  return (
    <div style={{
      background: "var(--s1)", border: "1px solid var(--b1)",
      borderRadius: "var(--r)", marginBottom: 14,
    }}>
      {title && (
        <div style={{
          padding: "10px 16px", borderBottom: "1px solid var(--b1)",
          fontSize: 10, color: "var(--tx3)", letterSpacing: ".07em",
          textTransform: "uppercase", display: "flex",
          alignItems: "center", justifyContent: "space-between",
        }}>
          <span>{title}</span>
          {action}
        </div>
      )}
      <div style={noPad ? {} : { padding: 16 }}>{children}</div>
    </div>
  );
}

export function Btn({ variant = "ghost", onClick, disabled, children, small }) {
  const variants = {
    orange: { background: "var(--orange)", color: "#fff" },
    blue:   { background: "var(--blue)", color: "#fff" },
    green:  { background: "var(--green)", color: "#fff" },
    danger: { background: "var(--red)", color: "#fff" },
    warn:   { background: "transparent", color: "var(--warn)", border: "1px solid var(--warn)" },
    ghost:  { background: "transparent", color: "var(--tx2)", border: "1px solid var(--b2)" },
    purple: { background: "var(--purple-dim)", color: "var(--purple)", border: "1px solid rgba(139,126,248,.3)" },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: small ? "4px 10px" : "8px 14px",
        borderRadius: "var(--r)", border: "none",
        fontFamily: "var(--mono)", fontSize: small ? 10 : 11,
        fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1, transition: "all .15s",
        ...variants[variant],
      }}
    >
      {children}
    </button>
  );
}

export function Badge({ type = "info", children }) {
  const types = {
    ok:     { bg: "var(--green-dim)",  color: "var(--green)" },
    fail:   { bg: "var(--red-dim)",    color: "var(--red)" },
    warn:   { bg: "var(--warn-dim)",   color: "var(--warn)" },
    info:   { bg: "var(--blue-dim)",   color: "var(--blue)" },
    purple: { bg: "var(--purple-dim)", color: "var(--purple)" },
    active: { bg: "var(--orange-dim)", color: "var(--orange)" },
    gray:   { bg: "rgba(255,255,255,0.05)", color: "var(--tx2)" },
  };
  const s = types[type] || types.info;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      padding: "2px 7px", borderRadius: 999, fontSize: 10,
      background: s.bg, color: s.color,
    }}>
      {children}
    </span>
  );
}

export function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 10, color: "var(--tx3)", letterSpacing: ".05em", textTransform: "uppercase" }}>{label}</div>
      {children}
    </div>
  );
}

export function Input({ value, onChange, placeholder, type = "text", readOnly }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{
        width: "100%", background: "var(--bg)", border: "1px solid var(--b2)",
        borderRadius: "var(--r)", color: "var(--tx)",
        fontFamily: "var(--mono)", fontSize: 12,
        padding: "7px 10px", outline: "none",
      }}
      onFocus={e => e.target.style.borderColor = "var(--orange)"}
      onBlur={e => e.target.style.borderColor = "var(--b2)"}
    />
  );
}

export function Select({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        width: "100%", background: "var(--bg)", border: "1px solid var(--b2)",
        borderRadius: "var(--r)", color: "var(--tx)",
        fontFamily: "var(--mono)", fontSize: 12,
        padding: "7px 10px", outline: "none", cursor: "pointer",
      }}
    >
      {children}
    </select>
  );
}

export function Table({ cols, rows, onRowClick }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th key={i} style={{
              textAlign: "left", fontSize: 10, color: "var(--tx3)",
              padding: "7px 10px", borderBottom: "1px solid var(--b1)",
              letterSpacing: ".06em", textTransform: "uppercase",
            }}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}
            onClick={() => onRowClick && onRowClick(row)}
            style={{ cursor: onRowClick ? "pointer" : "default" }}
            onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = "var(--s2)"; }}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            {row.cells.map((cell, j) => (
              <td key={j} style={{
                padding: "9px 10px", fontSize: 11,
                borderBottom: i < rows.length - 1 ? "1px solid var(--b1)" : "none",
                color: "var(--tx2)",
              }}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PageHeader({ title, sub, actions }) {
  return (
    <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div>
        <div style={{ fontFamily: "var(--display)", fontSize: 17, fontWeight: 600, color: "var(--tx)", marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--tx3)" }}>{sub}</div>
      </div>
      {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
    </div>
  );
}

export function StatGrid({ stats }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 10, marginBottom: 14 }}>
      {stats.map((s, i) => (
        <div key={i} style={{ background: "var(--s1)", border: "1px solid var(--b1)", borderRadius: "var(--r)", padding: "12px 14px" }}>
          <div style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 600, color: s.color || "var(--tx)", marginBottom: 2 }}>{s.value}</div>
          <div style={{ fontSize: 10, color: "var(--tx3)", letterSpacing: ".04em" }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

export function ConfirmBox({ title, body, onConfirm, confirmLabel = "Confirm", loading }) {
  return (
    <div style={{
      background: "var(--orange-dim)", border: "1px solid rgba(232,98,42,.3)",
      borderRadius: "var(--r)", padding: "14px 16px", marginTop: 12,
    }}>
      <div style={{ fontSize: 11, color: "var(--orange2)", marginBottom: 5, fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: 11, color: "var(--tx2)", marginBottom: 12, lineHeight: 1.6 }}>{body}</div>
      <Btn variant="danger" onClick={onConfirm} disabled={loading}>
        {loading && <Spinner size={10} />}
        {confirmLabel}
      </Btn>
    </div>
  );
}

export function ThreatIntelBanner({ matches, onDismiss }) {
  if (!matches || matches.length === 0) return null;
  return (
    <div style={{
      background: "var(--warn-dim)", border: "1px solid rgba(212,144,10,.35)",
      borderRadius: "var(--r)", padding: "12px 16px", marginBottom: 14,
    }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: "var(--warn)", marginBottom: 6 }}>
        ⚠ Threat intelligence: {matches.length} previous incident{matches.length > 1 ? "s" : ""} matched this sender / domain
      </div>
      {matches.slice(0, 3).map((m, i) => (
        <div key={i} style={{ fontSize: 10, color: "var(--tx3)", marginBottom: 3 }}>
          {m.date?.substring(0, 10)} — {m.action} by {m.operator}{m.ticketRef ? ` (${m.ticketRef})` : ""}
        </div>
      ))}
      <div style={{ marginTop: 8, fontSize: 10, color: "var(--tx2)" }}>
        You may proceed — this warning is for awareness only.
        {onDismiss && <span onClick={onDismiss} style={{ marginLeft: 12, color: "var(--warn)", cursor: "pointer", textDecoration: "underline" }}>Dismiss</span>}
      </div>
    </div>
  );
}

export function TicketRefInput({ value, onChange }) {
  return (
    <Field label="HaloITSM ticket ref (optional)">
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="e.g. INC-2024-047"
      />
    </Field>
  );
}
